import type { ListCriterion } from '@/api/lists'
import type { Member } from '@/api/users'

/** Field/operator metadata for dynamic-list criteria — mirrors backend ContactSegmentQuery. */

export const CUSTOM_PREFIX = 'custom.'

/** A blank first filter for a new dynamic list. */
export const emptyCriterion = (): ListCriterion => ({ field: 'owner_user_id', op: 'eq', value: '' })

export const CORE_FIELDS = [
  { value: 'owner_user_id', label: 'Owner' },
  { value: 'lead_source', label: 'Lead source' },
  { value: 'do_not_email', label: 'Do not email' },
  { value: 'do_not_sms', label: 'Do not SMS' },
  { value: 'submitted_form', label: 'Filled in a form' },
]

const FIELD_OPS: Record<string, string[]> = {
  owner_user_id: ['eq', 'ne', 'is_set'],
  lead_source: ['eq', 'ne', 'contains', 'is_set'],
  do_not_email: ['eq', 'ne', 'is_true', 'is_false'],
  do_not_sms: ['eq', 'ne', 'is_true', 'is_false'],
  submitted_form: ['eq', 'ne'],
}
const CUSTOM_OPS = ['eq', 'ne', 'contains', 'is_true', 'is_false', 'is_set']

const OP_LABELS: Record<string, string> = {
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
  is_true: 'is true',
  is_false: 'is false',
  is_set: 'is set',
}

export function isCustomField(field: string): boolean {
  return field.startsWith(CUSTOM_PREFIX)
}

export function customKey(field: string): string {
  return isCustomField(field) ? field.slice(CUSTOM_PREFIX.length) : field
}

export function isBooleanField(field: string): boolean {
  return field === 'do_not_email' || field === 'do_not_sms'
}

export function opsFor(field: string): string[] {
  return isCustomField(field) ? CUSTOM_OPS : (FIELD_OPS[field] ?? CUSTOM_OPS)
}

/** Words for a condition; "Filled in a form" reads "has filled in" / "hasn't filled in". */
export function opLabel(op: string, field?: string): string {
  if (field === 'submitted_form') return op === 'ne' ? 'hasn’t filled in' : 'has filled in'
  return OP_LABELS[op] ?? op
}

/** eq | ne | contains carry a value; is_true | is_false | is_set do not. */
export function opNeedsValue(op: string): boolean {
  return op === 'eq' || op === 'ne' || op === 'contains'
}

/** True when the criterion is complete enough to send (backend would 400 otherwise). */
export function criterionComplete(c: ListCriterion): boolean {
  if (isCustomField(c.field) && customKey(c.field).trim() === '') return false
  if (opNeedsValue(c.op)) return String(c.value ?? '').trim() !== ''
  return true
}

/** Strips the value from no-value ops so the payload stays minimal. */
export function cleanCriterion(c: ListCriterion): ListCriterion {
  return opNeedsValue(c.op) ? { field: c.field, op: c.op, value: c.value } : { field: c.field, op: c.op }
}

function fieldLabel(field: string): string {
  return CORE_FIELDS.find((f) => f.value === field)?.label ?? customKey(field)
}

/** One filter in words, e.g. "Owner is Jane Doe" or "Has filled in OM – 39 Grand Parade". */
export function criterionLabel(c: ListCriterion, members?: Member[], forms?: { id: string; name: string }[]): string {
  if (c.field === 'submitted_form') {
    const name = forms?.find((f) => f.id === c.value)?.name ?? 'a form'
    return `${c.op === 'ne' ? 'Hasn’t' : 'Has'} filled in ${name}`
  }
  let value = ''
  if (opNeedsValue(c.op)) {
    value = String(c.value ?? '')
    if (c.field === 'owner_user_id') {
      value = members?.find((u) => u.id === value)?.fullName ?? value
    }
    value = ` ${value}`
  }
  return `${fieldLabel(c.field)} ${opLabel(c.op)}${value}`
}

/** Human-readable one-liner, e.g. "Owner is Jane Doe and Lead source contains web". */
export function criteriaSummary(criteria: ListCriterion[], members?: Member[], forms?: { id: string; name: string }[]): string {
  if (criteria.length === 0) return 'All contacts'
  return criteria.map((c) => criterionLabel(c, members, forms)).join(' and ')
}
